import { AddUpdateProductsForLocationRequestDto } from "src/app/types/api/api-request-dtos";
import { ProductsLookupResponseDto } from "src/app/types/api/api-response-dtos";
import { BusinessType, Products } from "src/app/types/api/registration.types";
import {
  ProductInternal,
  ProductLookupInternal,
} from "src/app/types/local/product.types";

export const isValidObject = (obj) => {
  return Object.values(obj).every((value) => {
    if (typeof value === "object" && !Array.isArray(value) && value !== null) {
      return isValidObject(value);
    }
    return (
      (typeof value === "string" || Array.isArray(value)) &&
      value != null &&
      value.length > 0
    );
  });
};

export function dataURLtoFile(dataurl: any, filename: string) {
  var arr = dataurl.split(","),
    mime = arr[0].match(/:(.*?);/)[1],
    bstr = atob(arr[1]),
    n = bstr.length,
    u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new File([u8arr], filename + "." + mime.split("/")[1], { type: mime });
}

export function serverProductsLookupToInternal(
  serverProducts: ProductsLookupResponseDto[],
  businessType: BusinessType
): ProductLookupInternal[] {
  // create a new array of string which has all the category names, should be unique
  const categories: string[] = serverProducts.map(
    (product) => product.category
  );
  // remove duplicates
  const uniqueCategories = Array.from(new Set(categories));

  // create a new array of ProductLookupInternal

  const productsInternal: ProductLookupInternal[] = uniqueCategories.map(
    (category) => {
      const products: ProductInternal[] = serverProducts
        .filter((product) => product.category === category)
        .map((product) => {
          const internalDetails = productDetails(businessType).find(
            (i) => i.lookupId === product.lookupId
          );
          return {
            lookupId: product.lookupId,
            productName: product.productName,
            imageUrl: product.imageUrl,
            minCount: product.defaultCount,
            maxCount: product.maximumCount,
            price:
              businessType === BusinessType.Freelancer
                ? +product?.price?.priceForFreelancer > 0
                  ? "65.22"
                  : "free"
                : "free",
            hasDetails: true,
            popupDetails: internalDetails?.popupDetails,
            pricingDetails: internalDetails?.pricingDetails,

            type: internalDetails?.type,
            hasPriceTooltip: internalDetails?.hasPriceTooltip,
            count: internalDetails?.count,
            isRemoved: false,
            category: product.category,
          };
        });
      return { category, products };
    }
  );

  return productsInternal;
}

export function productInternalToServerProductBody(
  productLookupInternal: ProductLookupInternal[]
): AddUpdateProductsForLocationRequestDto {
  const allProductsFlatten = productLookupInternal.flatMap(
    (productsCategorized) => productsCategorized.products
  );

  const productsRequestDTO = allProductsFlatten.map((product) => {
    return {
      productName: product.productName,
      lookupId: product.lookupId,
      productCount: product.count,
      productType: product.type,
      productCategory: product.category,
      isRemoved: product.isRemoved,
    };
  });

  return {
    products: [...productsRequestDTO],
  };
}

export function serverLocationProductsToSummary(
  currentProducts: Products[],
  productsLookup: ProductLookupInternal[]
): ProductLookupInternal[] {
  if (!currentProducts || !currentProducts.length) {
    return productsLookup;
  }
  const productsFlattened = productsLookup.flatMap(
    (productCategorized) => productCategorized.products
  );

  productsFlattened.forEach((product) => {
    const productFound = currentProducts.find(
      (productSelected) => productSelected.lookupId === product.lookupId
    );

    if (productFound) {
      if (productFound.isRemoved) {
        product.isRemoved = true;
        product.count = 0;
      } else {
        product.count = productFound.productCount;
      }
    }
  });

  return productsLookup;
}

export const productDetails = (
  businessType: BusinessType
): Array<ProductInternal> => [
  {
    lookupId: 8800,
    type: "included",
    count: undefined,
    hasPriceTooltip: false,
    popupDetails: {
      title: "IBAN account",
      description: "Instantly access, spend, and manage your sales",
      imageUrl: "/images/location-products/iban-account-details.png",
      features: [
        {
          title: "Full cash-in cash-out features",
          description:
            "Works like a bank account, but even better. Get instant access to your POS sales, make ATM withdrawals, send transfers, or make SADAD payments.",
        },
        {
          title: "Account financing",
          description:
            "Get a customized loan based on your card sales and pay monthly through your account balance.",
        },
        {
          title: "Instant wallet-to-wallet payments with 0% fees",
          description:
            "Instant wallet-to-wallet payments with zero fees for fast and seamless transfers.",
        },
      ],
    },
  },
  {
    lookupId: 8801,
    popupDetails: {
      title: "Digital debit card (VISA)",
      description: "Shop and spend with ease",
      imageUrl: "/images/location-products/digital-debit-visa.png",
      features: [
        {
          title: "Payments made easier",
          description:
            "Instantly access your funds for shopping and payments online and in-store with no delays, with easy integration with Apply Pay and Samsung Pay.",
        },
        {
          title: "The full benefits of VISA cards",
          description:
            "Compatible with global payment networks for seamless local and international transactions.",
        },
        {
          title: "Smart and secure",
          description:
            "Enhanced security with two-factor authentication and instant transaction alerts via the Hala app.",
        },
      ],
    },
    type: "included",
    count: undefined,
  },
  {
    lookupId: 8802,
    popupDetails: {
      title: "Physical debit card (MADA)",
      description: "Access and withdraw your money anytime anywhere",
      imageUrl: "/images/location-products/physical-debit-mada.png",
      features: [
        {
          title: "Local transactions made easy",
          description:
            "Choose from 18,000 ATMs and shop at over 700,000 outlets across KSA.",
        },
        {
          title: "Direct account access",
          description:
            "Seamlessly connect to your Hala account for instant transactions anywhere.",
        },
        {
          title: "Enhanced transaction security",
          description:
            "Benefit from advanced security features designed for safe spending within the Kingdom.",
        },
      ],
    },
    type: "counter",
    count: 1,
  },
  {
    lookupId: 8803,
    popupDetails: {
      title: "Physical debit card (VISA)",
      description: "Access and withdraw your money anytime anywhere",
      imageUrl: "/images/location-products/physical-debit-visa-details.png",
      features: [
        {
          title: "Access your funds globally",
          description:
            "Withdraw cash in over 200 countries and spend at 130 million merchants worldwide.",
        },
        {
          title: "Linked directly to your account",
          description:
            "Access your funds instantly, with seamless integration to your Hala account.",
        },
        {
          title: "Manage with full control",
          description:
            "Set limits, track spending, and freeze your card anytime through the Hala app.",
        },
      ],
    },
    type: "counter",
    count: 1,
  },
  {
    lookupId: 8804,
    popupDetails: {
      title: "POS terminal",
      description:
        "Accept contactless cards and digital wallets on iPhone or on Android",
      imageUrl: "/images/location-products/pos-details.png",
      features: [
        {
          title: "Dual 6.5’ LED screen",
          description: "6.5’ Full-view Water-drop screen design",
        },
        {
          title: "Hi-Resolution display",
          description:
            "1600 × 720 HD resolution for clearer image and video display",
        },
        {
          title: "Hi-Speed printer",
          description: "95mm/s Printer speed 58mm Thermal Printing",
        },
        {
          title: "30 Hours battery",
          description: "Сan standby for 30 hours on a full charge",
        },
      ],
    },
    pricingDetails: {
      title: "Pricing",
      imageUrl: "/images/location-products/pricing-icon.png",
      features: [
        {
          title: "Monthly subscription per terminal",
          subTitle:
            businessType === BusinessType.Freelancer ? "SAR 65.22" : "SAR 99",
          description: `if transaction volume is ${
            businessType === BusinessType.Freelancer ? "more" : "less"
          } than SAR 15k/month`,
        },
        {
          title: "Terminal one time set up",
          subTitle:
            businessType === BusinessType.Freelancer ? "SAR 65.22" : "Free",
        },
      ],
      feesTitle: "POS transaction fees",
      fees: [
        {
          title: "Mada",
          pricing: "0.8%",
          description: "commission is capped at SAR 160",
          isVisible: businessType === BusinessType.Freelancer ? true : false,
        },
        {
          title: "GCC network",
          pricing: "2% ",
          description: "commission is capped at SAR 37.5",
          isVisible: true,
        },
      ],
      madaCards: {
        title: "Mada Cards",
        pricing: [
          {
            pricing: "0.7%",
            description: "for transactions below SAR 100",
          },
          {
            pricing: "0.8%",
            description: "for transactions equal to or above SAR 100",
          },
        ],
      },
      otherCards: {
        title: "Visa / Mastercard / Amex",
        pricing: [
          {
            pricing: "2.25% + SAR 1",
            description: "for transactions equal to or below SAR 250",
          },
          {
            pricing: "2.25%",
            description: "for transactions above SAR 250",
          },
        ],
      },
    },
    type: "counter",
    count: 1,
    hasPriceTooltip: true,
  },
  {
    lookupId: 8805,
    popupDetails: {
      title: "Phone POS",
      description: "Accept payments on your Android phone",
      imageUrl: "/images/location-products/soft-pos-details.png",
      features: [
        {
          title: "Accept payments on the go",
          description:
            "Turn your smartphone into a POS device and accept contactless cards, Apple Pay, Google Pay, and other digital wallets.",
        },
        {
          title: "No hardware required",
          description:
            "Start accepting payments instantly with just your phone—no additional devices needed.",
        },
        {
          title: "Seamless business integration",
          description:
            "Track and manage your transactions easily through your Hala account, all in one place.",
        },
      ],
    },
    type: "addRemove",
    count: 1,
    hasPriceTooltip: true,
  },
  {
    lookupId: 8806,
    popupDetails: {
      title: "Payment link",
      description: "Get paid remotely with a single link",
      imageUrl: "/images/location-products/payment-link-details.png",
      features: [
        {
          title: "Accept payments remotely",
          description:
            "Share a payment link via SMS or email, and let customers pay from anywhere.",
        },
        {
          title: "Quick and easy setup",
          description:
            "Generate payment links in seconds through the Hala app, no technical expertise needed.",
        },
        {
          title: "Safe and secure transactions",
          description:
            "Enjoy peace of mind with end-to-end encryption and secure payment processing.",
        },
      ],
    },
    type: "addRemove",
    hasPriceTooltip: true,
    count: 1,
  },
];

export function getCampaignMetadata(): Record<string, string | null> {
  const keys = JSON.parse(
    localStorage.getItem("queryParamsKeys") || "[]"
  ) as string[];
  const queryParams: Record<string, string | null> = {};

  for (const key of keys) {
    queryParams[key] = localStorage.getItem(key) || null;
  }
  queryParams["fullUrl"] = localStorage.getItem("fullUrl") || null;
  queryParams["source"] = localStorage.getItem("source") || null;

  return queryParams;
}

export function removeCampaignMetadata() {
  const keys = JSON.parse(
    localStorage.getItem("queryParamsKeys") || "[]"
  ) as string[];

  for (const key of keys) {
    localStorage.removeItem(key);
  }

  localStorage.removeItem("queryParamsKeys");
  localStorage.removeItem("fullUrl");
}

export function isMobileDevice() {
  if (navigator["userAgentData"]?.mobile) {
    return true;
  }
  if (window.matchMedia("(max-width: 768px)").matches) {
    return true;
  }
  return /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

export enum OS {
  Windows = "Windows",
  MacOS = "MacOS",
  Linux = "Linux",
  Android = "Android",
  iOS = "iOS",
  Unknown = "Unknown OS",
}

export function getOS(): OS {
  const userAgent = window.navigator.userAgent;
  const userAgentData = (window.navigator as any).userAgentData;

  if (userAgentData?.platform) {
    if (/Win/i.test(userAgentData.platform)) return OS.Windows;
    if (/Mac/i.test(userAgentData.platform)) return OS.MacOS;
    if (/Linux/i.test(userAgentData.platform)) return OS.Linux;
  }

  if (/Android/i.test(userAgent)) return OS.Android;
  if (/iPhone|iPad|iPod/i.test(userAgent)) return OS.iOS;

  return OS.Unknown;
}

export function calculateProductPrices(priceValue, count: number) {
  if (!priceValue || isNaN(count)) return 0;

  const price =
    typeof priceValue === "number"
      ? priceValue
      : parseFloat(priceValue.replace(/[^\d.]/g, ""));
  return isNaN(price) ? 0 : price * count;
}


export function mapEntityPermissions(permissions: any[]) {
  const entityPermissions = permissions.map(
    ({
      lookupId,
      name,
      label,
      description,
      isEnabled,
      controlProperties = [],
    }) => ({
      lookupId,
      name,
      label,
      description,
      isEnabled: isEnabled,
      controlPropertiesList: controlProperties.map(
        ({ lookupId, isEnabled }) => ({ lookupId, isEnabled })
      ),
    })
  );
  return entityPermissions || [];
}


export const titles = [
  { label: "Mr", value: "Mr" },
  { label: "Mrs", value: "Mrs" },
  { label: "Ms", value: "Ms" },
  { label: "Dr", value: "Dr" },
];

